import React, { useCallback, useState } from 'react';
import { formatDate, formatDateTime } from '../../lib/formatDate';
import { errorMessage, toastError, toastSuccess } from '../../lib/toast';
import { getMessages, postMessage } from './vendorsApi';
import {
  PROPOSAL_KIND_LABEL, PROPOSAL_STATUS_LABEL, VENDOR_ERRORS, fieldValueLabel, odooDateTime,
} from './vendorLabels';
import { Skeleton, InlineError, ProposalOutcome } from './VendorBits';
import { useOdooList } from './VendorDetailPanels';

const COMMENT_MAX = 2000;

const STATUS_TONE = { approved: 'ok', pending: 'info', rejected: 'bad', failed: 'bad', stale: 'bad', cancelled: 'muted' };

const stamp = (v) => {
  const t = Date.parse(v);
  return Number.isNaN(t) ? 0 : t;
};

// One timeline from two sources: Odoo chatter (comments and tracked field
// changes) and Nexus proposals. Each source can fail on its own; a failed
// source is reported, never treated as "nothing happened".
function merge(messages, proposals) {
  const items = [
    ...messages.map((m) => ({ type: m.kind === 'tracking' ? 'tracking' : 'note', at: odooDateTime(m.date || m.created_at), data: m, id: `m${m.id}` })),
    ...proposals.map((p) => ({ type: 'proposal', at: p.created_at, data: p, id: `p${p.id}` })),
  ];
  return items.sort((a, b) => stamp(b.at) - stamp(a.at));
}

function Entry({ item }) {
  const { data } = item;
  if (item.type === 'proposal') {
    const kind = PROPOSAL_KIND_LABEL[data.kind] || data.kind;
    return (
      <li className="vd-event vd-event--proposal">
        <p className="vd-event-head">
          <strong>{data.proposer_name || 'Someone'}</strong> proposed: {kind}
          <span className={`vd-status vd-status--${STATUS_TONE[data.status] || 'muted'}`}>{PROPOSAL_STATUS_LABEL[data.status] || data.status}</span>
        </p>
        <p className="vd-event-meta">
          {formatDate(data.created_at)}
          {data.resolver_name && ` · ${data.status === 'approved' ? 'Approved' : 'Decided'} by ${data.resolver_name}`}
          {data.resolved_at && ` on ${formatDate(data.resolved_at)}`}
        </p>
        <ProposalOutcome proposal={data} />
      </li>
    );
  }
  const who = data.author_name || data.author || 'Odoo';
  return (
    <li className={`vd-event vd-event--${item.type}`}>
      <p className="vd-event-head">
        <strong>{who}</strong> {item.type === 'tracking' ? 'changed in Odoo' : 'commented'}
      </p>
      <p className="vd-event-meta">{formatDateTime(item.at)}</p>
      {item.type === 'tracking' && Array.isArray(data.changes) ? (
        <ul className="vd-diff" aria-label="Changes in Odoo">
          {data.changes.map((c) => (
            <li key={c.field || c.label}>
              {c.label || c.field}: <span className="vd-from">{fieldValueLabel(c.field, c.from)}</span>{' → '}<strong>{fieldValueLabel(c.field, c.to)}</strong>
            </li>
          ))}
        </ul>
      ) : (
        <p className="vd-comment-body">{data.body ?? data.text ?? ''}</p>
      )}
    </li>
  );
}

function Composer({ vendorId, onPosted }) {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body || busy) return;
    setBusy(true);
    try {
      await postMessage(vendorId, body);
      toastSuccess('Comment added.');
      setDraft('');
      onPosted();
    } catch (err) {
      toastError(errorMessage(err, 'Could not add the comment.', VENDOR_ERRORS));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="vd-composer">
      <label htmlFor="vd-comment" className="sr-only">Add a comment</label>
      <textarea
        id="vd-comment"
        rows={2}
        maxLength={COMMENT_MAX}
        placeholder="Add a comment for everyone who can see this vendor"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
      />
      <div className="vd-composer-foot">
        <span className="vd-counter">{draft.length > 0 ? `${draft.length}/${COMMENT_MAX}` : 'Posted to the vendor in Odoo'}</span>
        <button type="submit" className="btn btn-primary btn-small" disabled={busy || !draft.trim()} aria-busy={busy || undefined}>Add comment</button>
      </div>
    </form>
  );
}

/** Right rail: what happened to this vendor, newest first, with the comment box on top. */
export default function VendorActivity({ vendorId, canComment, history }) {
  const fetcher = useCallback(() => getMessages(vendorId), [vendorId]);
  const [reload, setReload] = useState(0);
  const messages = useOdooList(fetcher, 'messages', reload);

  const loading = messages.status === 'loading' || history.status === 'loading';
  const items = merge(
    messages.status === 'ready' ? messages.items : [],
    history.status === 'ready' ? history.items : [],
  );
  const anyFailed = messages.status === 'error' || history.status === 'error';

  return (
    <section className="vd-panel vd-activity" aria-labelledby="vd-activity-title">
      <header className="vd-panel-head">
        <h2 id="vd-activity-title" className="vd-panel-title">Activity</h2>
      </header>
      {canComment && <Composer vendorId={vendorId} onPosted={() => setReload((n) => n + 1)} />}
      {messages.status === 'error' && <InlineError message="Couldn't reach Odoo for comments right now." />}
      {history.status === 'error' && <InlineError message="Couldn't load the change history. Try again in a minute." />}
      {loading && items.length === 0 && <Skeleton rows={3} label="Loading activity" />}
      {!loading && items.length === 0 && !anyFailed && <p className="vd-muted vd-activity-empty">No comments or changes yet.</p>}
      {items.length > 0 && (
        <ul className="vd-timeline" aria-label="Activity timeline">
          {items.map((it) => <Entry key={it.id} item={it} />)}
        </ul>
      )}
    </section>
  );
}
